import { useState, useEffect } from 'react';
import { PipelineRunDto } from '../../types/pipelineFlow';
import { usePipelineRunDetails } from '../../services/queries';
import { LoadingOverlay } from '../common';
import { WorkItemsSection } from './WorkItemsSection';
import { PullRequestsSection } from './PullRequestsSection';
import { PipelineStagesPanel } from '../pipeline/PipelineStagesPanel';

interface PipelineRunCardProps {
  run: PipelineRunDto;
  gitHubRepository?: string;
  isExpanded?: boolean;
  onToggleDetails?: () => void;
}

export function PipelineRunCard({ 
  run, 
  gitHubRepository,
  isExpanded = false, 
  onToggleDetails
}: PipelineRunCardProps) {
  const [showDetails, setShowDetails] = useState(isExpanded);
  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);
  
  // Only fetch individual run details when user explicitly requests them
  const { data: currentRunDetails, isLoading: isCurrentlyLoading } = usePipelineRunDetails(
    run.buildId, 
    shouldFetchDetails
  );

  // Sync with parent expanded state
  useEffect(() => {
    setShowDetails(isExpanded);
  }, [isExpanded]);

  const handleToggleDetails = () => {
    const newShowDetails = !showDetails;
    setShowDetails(newShowDetails);
    
    // Only fetch details when expanding (not when collapsing)
    if (newShowDetails) {
      setShouldFetchDetails(true);
    }
    
    if (onToggleDetails) {
      onToggleDetails();
    }
  };

  // Normalize status and result to lowercase for matching
  const normalizedStatus = run.status?.toLowerCase() || '';
  const normalizedResult = run.result?.toLowerCase() || '';
  
  // Use the new badge system with proper status colors
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'succeeded':
      case 'completed':
        return 'badge badge-success';
      case 'failed':
        return 'badge bg-danger-500 text-white'; // Strong red for failed
      case 'inprogress':
      case 'running':
        return 'badge bg-primary-500 text-white'; // Strong blue for in-progress
      case 'queued':
      case 'pending':
        return 'badge badge-warning';
      case 'canceled':
      case 'cancelled':
      case 'abandoned':
      default:
        return 'badge badge-gray';
    }
  };
  
  const getResultBadgeClass = (result: string) => {
    switch (result) {
      case 'succeeded':
        return 'badge badge-success';
      case 'failed':
        return 'badge bg-danger-500 text-white';
      case 'partiallysucceeded':
        return 'badge badge-warning';
      case 'canceled':
      case 'cancelled':
      case 'abandoned':
      case 'none':
      default:
        return 'badge badge-gray';
    }
  };
  
  const statusBadgeClass = getStatusBadgeClass(normalizedStatus);
  const resultBadgeClass = getResultBadgeClass(normalizedResult);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h4 className="text-lg font-semibold text-gray-900">{run.buildName}</h4>
            <span className={statusBadgeClass}>
              {run.status}
            </span>
            <span className={resultBadgeClass}>
              {run.result}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500">Requested by {run.requestedFor}</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleToggleDetails}
            className="text-sm font-medium text-blue-600 hover:text-blue-500 flex items-center"
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
            <svg 
              className={`ml-1 h-4 w-4 transform transition-transform ${showDetails ? 'rotate-180' : ''}`}
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={1.5} 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </button>
          <a
            href={run.buildUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            View Build →
          </a>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 text-sm text-gray-500 md:grid-cols-4">
        <div>
          <span className="font-medium">Build ID:</span> {run.buildId}
        </div>
        <div>
          <span className="font-medium">Started:</span>{' '}
          {new Date(run.startTime).toLocaleString()}
        </div>
        {run.sourceBranch && (
          <div>
            <span className="font-medium">Branch:</span>{' '}
            <span className={`font-mono ${
              run.sourceBranch.toLowerCase().includes('/pull/') 
                ? 'text-purple-600' 
                : run.sourceBranch.toLowerCase().includes('main') || run.sourceBranch.toLowerCase().includes('master')
                ? 'text-green-600'
                : 'text-blue-600'
            }`}>
              {run.sourceBranch.replace('refs/heads/', '')}
              {run.sourceBranch.toLowerCase().includes('/pull/') && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  PR
                </span>
              )}
            </span>
          </div>
        )}
        {run.finishTime && (
          <div>
            <span className="font-medium">Finished:</span>{' '}
            {new Date(run.finishTime).toLocaleString()}
          </div>
        )}
        {run.duration && (
          <div>
            <span className="font-medium">Duration:</span> {run.duration}
          </div>
        )}
      </div>

      {/* Expandable Details Section */}
      {showDetails && (
        <div className="mt-6 border-t pt-6">
          <LoadingOverlay
            isLoading={isCurrentlyLoading}
            loadingText="Loading pipeline details..."
            size="sm"
            debouncedOptions={{
              showDelay: 200,
              minDisplayTime: 400,
              cooldownPeriod: 100
            }}
          >
            {currentRunDetails ? (
              <div className="space-y-6">
                {/* Pipeline Stages */}
                <PipelineStagesPanel buildId={run.buildId} />

                {/* Source Information */}
                {(currentRunDetails.sourceBranch || currentRunDetails.sourceVersion) && (
                  <div>
                    <h5 className="text-sm font-semibold text-gray-900 mb-3">Source Information</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {currentRunDetails.sourceBranch && (
                        <div>
                          <span className="font-medium text-gray-700">Branch:</span>{' '}
                          <span className={`text-gray-600 font-mono ${
                            currentRunDetails.sourceBranch.toLowerCase().includes('/pull/') 
                              ? 'text-purple-600' 
                              : currentRunDetails.sourceBranch.toLowerCase().includes('main') || currentRunDetails.sourceBranch.toLowerCase().includes('master')
                              ? 'text-green-600'
                              : 'text-gray-600'
                          }`}>
                            {currentRunDetails.sourceBranch}
                          </span>
                          {currentRunDetails.sourceBranch.toLowerCase().includes('/pull/') && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              PR
                            </span>
                          )}
                        </div>
                      )}
                      {currentRunDetails.sourceVersion && (
                        <div>
                          <span className="font-medium text-gray-700">Commit:</span>{' '}
                          {gitHubRepository ? (
                            <a
                              href={`https://github.com/${gitHubRepository}/commit/${currentRunDetails.sourceVersion}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 font-mono text-sm"
                            >
                              {currentRunDetails.sourceVersion.substring(0, 8)}
                            </a>
                          ) : (
                            <span className="text-gray-600 font-mono">{currentRunDetails.sourceVersion.substring(0, 8)}...</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Pull Requests */}
                <PullRequestsSection pullRequests={currentRunDetails.pullRequests} />

                {/* Work Items */}
                <WorkItemsSection workItems={currentRunDetails.workItems} />
              </div>
            ) : (
              <div className="text-sm text-gray-500 py-4 text-center">
                Failed to load additional details.
              </div>
            )}
          </LoadingOverlay>
        </div>
      )}
    </div>
  );
}